import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { FiAlertTriangle, FiCheck, FiClock, FiUser, FiX } from "react-icons/fi";

const formatDistanceToNow = (dateString: string) => {
  const diff = Date.now() - new Date(dateString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

interface SosAlert {
  id: number;
  studentId: number;
  status: "Active" | "Resolved";
  createdAt: string;
  student: {
    id: number;
    firstName: string;
    lastName: string;
    grade: string;
    section: string;
    userId: number;
  };
}

const SosAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<SosAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolvingAlert, setResolvingAlert] = useState<SosAlert | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/sos/active`);
      if (res.data.success) {
        setAlerts(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch SOS alerts:", err);
      setError("Failed to fetch active SOS alerts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    // Poll every 30 seconds for new alerts
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleResolveClick = (alert: SosAlert) => {
    setResolvingAlert(alert);
    setResolutionNotes("");
    setResolveModalOpen(true);
  };

  const submitResolution = async () => {
    if (!resolvingAlert) return;
    setSubmitting(true);
    try {
      // Typically we would send `resolvedBy` from auth context, but for now we'll pass 1 or omit
      await axios.put(`${API_BASE_URL}/sos/${resolvingAlert.id}/resolve`, {
        resolutionNotes,
        resolvedBy: 1 // TODO: Replace with actual logged-in user ID
      });
      setResolveModalOpen(false);
      setResolvingAlert(null);
      fetchAlerts(); // Refresh the list
    } catch (err) {
      console.error("Failed to resolve alert:", err);
      alert("An error occurred while resolving the alert.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && alerts.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FiAlertTriangle className="text-red-500" /> SOS Alerts
          </h1>
          <p className="text-gray-500 text-sm mt-1">Manage emergency alerts triggered by students.</p>
        </div>
        <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg font-bold shadow-sm border border-red-100 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
          {alerts.length} Active Alerts
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100">
          {error}
        </div>
      )}

      {alerts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center flex flex-col items-center">
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
            <FiCheck size={32} className="text-green-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-800">No Active Alerts</h3>
          <p className="text-gray-500 mt-2">There are currently no active SOS alerts from students.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {alerts.map((alert) => (
            <div key={alert.id} className="bg-white rounded-2xl border border-red-200 shadow-sm overflow-hidden flex flex-col sm:flex-row">
              <div className="bg-red-50 p-4 sm:w-48 flex flex-col items-center justify-center border-b sm:border-b-0 sm:border-r border-red-100 text-center">
                <FiAlertTriangle size={32} className="text-red-500 mb-2" />
                <div className="text-sm font-bold text-red-700">EMERGENCY</div>
                <div className="text-xs text-red-500 mt-1 flex items-center gap-1 justify-center">
                  <FiClock size={12} />
                  {formatDistanceToNow(alert.createdAt)}
                </div>
              </div>
              <div className="p-5 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <FiUser className="text-gray-400" />
                    {alert.student.firstName} {alert.student.lastName}
                  </h3>
                  <div className="flex items-center gap-3 mt-2 text-sm text-gray-600">
                    <span className="bg-gray-100 px-2 py-1 rounded">Grade {alert.student.grade}</span>
                    <span className="bg-gray-100 px-2 py-1 rounded">Section {alert.student.section}</span>
                    <span className="text-gray-400 text-xs">ID: {alert.student.userId}</span>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => handleResolveClick(alert)}
                    className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg font-medium transition-colors shadow-sm active:scale-95 flex items-center gap-2"
                  >
                    <FiCheck /> Mark as Resolved
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Resolve Modal */}
      {resolveModalOpen && resolvingAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-5 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-800">Resolve SOS Alert</h2>
              <button onClick={() => setResolveModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FiX size={20} />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-4">
                You are resolving the emergency alert for <strong className="text-gray-800">{resolvingAlert.student.firstName} {resolvingAlert.student.lastName}</strong>.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resolution Notes
              </label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Briefly describe how this emergency was handled..."
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#1ecab8] focus:border-[#1ecab8] outline-none min-h-[120px]"
              />
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setResolveModalOpen(false)}
                className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={submitResolution}
                disabled={submitting || !resolutionNotes.trim()}
                className="px-5 py-2 bg-[#1ecab8] hover:bg-[#15b0a0] text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? "Resolving..." : "Confirm Resolution"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SosAlerts;
